/**
 * SocialPulseContact — /contact page.
 *
 * SocialPulse-branded contact form. Posts to `/api/contact`, shows a
 * gentle thank-you state on success, and offers direct email + trust-and-
 * safety escape hatches. Uses the SocialPulse palette (emerald / violet /
 * blue / orange / lavender) — no dark surfaces.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Mail, Send, ShieldCheck, MessageCircle, Building2, Newspaper,
  UserPlus, Handshake, Globe2, Check, Loader2, AlertCircle,
} from "lucide-react";
import { API } from "@/lib/api";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

const TOPICS = [
  { value: "general",          label: "General question",         icon: MessageCircle,tint: "violet"  },
  { value: "support",          label: "Help with my account",     icon: UserPlus,     tint: "emerald" },
  { value: "partnership",      label: "Partnership / business",   icon: Handshake,    tint: "blue"    },
  { value: "press",            label: "Press & media",            icon: Newspaper,    tint: "orange"  },
  { value: "trust-safety",     label: "Trust & safety report",    icon: ShieldCheck,  tint: "rose"    },
  { value: "network-request",  label: "Request a new place",      icon: Globe2,       tint: "teal"    },
  { value: "billing",          label: "Billing question",         icon: Building2,    tint: "orange"  },
  { value: "founder",          label: "For the founder",          icon: MessageCircle,tint: "violet"  },
];

export default function SocialPulseContact() {
  const [form, setForm] = useState({
    name: "", email: "", topic: "general", message: "", website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Contact SocialPulse — support, press, partnerships & trust";
    setMeta("description", "Get in touch with SocialPulse. General questions, account help, press, partnerships, trust & safety and place requests — all routed to the right team.");
    setLink("canonical", "https://socialpulse.com/contact");
    setMeta("robots", "index, follow");
  }, []);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (form.name.trim().length < 2) return setError("Please share your name.");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return setError("That doesn't look like a valid email.");
    if (form.message.trim().length < 10) return setError("Your message is a bit too short — add a few more details.");

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        site: "socialpulse",
      });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "We couldn't send your message right now. Please try again in a moment.";
      setError(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-white" data-testid="sp-contact-page">
      <SocialPulseHeader />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(140deg, #F0F6FF 0%, #F5F1FA 40%, #ECFDF5 100%)",
        }}
      >
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 pt-12 sm:pt-16 pb-10 sm:pb-14 relative">
          <div
            aria-hidden
            className="absolute -top-16 -left-16 w-72 h-72 rounded-full blur-3xl opacity-45 pointer-events-none"
            style={{ background: "radial-gradient(circle, #A7F3D0 0%, transparent 70%)" }}
          />
          <div
            aria-hidden
            className="absolute top-8 right-0 w-80 h-80 rounded-full blur-3xl opacity-40 pointer-events-none"
            style={{ background: "radial-gradient(circle, #DDD6FE 0%, transparent 75%)" }}
          />

          <div className="relative max-w-3xl">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[.22em] text-violet-700">
              <Mail className="w-3.5 h-3.5" /> Contact
            </div>
            <h1
              className="mt-3 font-serif font-bold tracking-tight text-slate-900 leading-[1.05] text-3xl sm:text-4xl lg:text-5xl"
              data-testid="sp-contact-h1"
            >
              Say hello. We <span className="text-emerald-700">listen</span>.
            </h1>
            <p className="mt-4 sm:mt-5 text-slate-600 text-base sm:text-lg leading-relaxed max-w-2xl">
              Pick a topic, drop us a note, and the right team will get back to
              you. For urgent safety concerns, use the trust & safety route
              below.
            </p>
          </div>
        </div>
      </section>

      <main className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 py-10 sm:py-14 grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-8 lg:gap-12">
        {/* Form */}
        <div>
          {done ? (
            <div
              className="rounded-3xl ring-1 ring-emerald-200 p-8 sm:p-10"
              style={{ background: "linear-gradient(135deg,#ECFDF5 0%, #F5F1FA 100%)" }}
              data-testid="sp-contact-success"
            >
              <div className="w-12 h-12 grid place-items-center rounded-2xl bg-gradient-to-br from-emerald-500 to-violet-600 text-white shadow-sm">
                <Check className="w-5 h-5" strokeWidth={3} />
              </div>
              <h2 className="mt-5 font-serif font-bold text-slate-900 text-2xl sm:text-3xl leading-tight">
                Message received.
              </h2>
              <p className="mt-2 text-slate-700 text-[15px] leading-relaxed max-w-lg">
                Thanks for reaching out — the right team will pick this up soon.
                If it's urgent, drop us a line directly at{" "}
                <a href="mailto:support@socialpulse.com" className="font-semibold text-violet-700 hover:text-violet-900">
                  support@socialpulse.com
                </a>.
              </p>
              <button
                onClick={() => { setDone(false); setForm({ name: "", email: "", topic: "general", message: "", website: "" }); }}
                data-testid="sp-contact-again"
                className="mt-6 inline-flex items-center gap-2 rounded-full ring-1 ring-slate-300 hover:ring-violet-400 text-slate-700 hover:text-violet-700 font-semibold px-5 py-2.5 text-sm transition-colors"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="sp-contact-form">
              <Field label="Your name" htmlFor="c-name">
                <input
                  id="c-name"
                  data-testid="sp-contact-name"
                  type="text"
                  autoComplete="name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  className={INPUT_CLS}
                  placeholder="Jane Rodriguez"
                  required
                />
              </Field>
              <Field label="Email" htmlFor="c-email">
                <input
                  id="c-email"
                  data-testid="sp-contact-email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className={INPUT_CLS}
                  placeholder="you@example.com"
                  required
                />
              </Field>

              <Field label="What's this about?">
                <ul
                  className="grid grid-cols-2 sm:grid-cols-4 gap-2"
                  data-testid="sp-contact-topics"
                >
                  {TOPICS.map((t) => <TopicChip key={t.value} {...t} selected={form.topic === t.value} onClick={() => set("topic", t.value)} />)}
                </ul>
              </Field>

              <Field label="Your message" htmlFor="c-msg">
                <textarea
                  id="c-msg"
                  data-testid="sp-contact-message"
                  rows={6}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  className={`${INPUT_CLS} py-3 resize-y min-h-[140px]`}
                  placeholder="A few sentences will do."
                  required
                />
              </Field>

              {/* Honeypot — hidden from real users, visible to bots. */}
              <div className="hidden" aria-hidden>
                <label>
                  Website
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => set("website", e.target.value)}
                  />
                </label>
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded-2xl bg-rose-50 ring-1 ring-rose-200 px-4 py-3 text-sm text-rose-800" data-testid="sp-contact-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3 pt-1">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="sp-contact-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white font-semibold px-5 py-2.5 text-sm transition-colors shadow-sm"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {submitting ? "Sending…" : "Send message"}
                </button>
                <p className="text-xs text-slate-500">
                  We reply from <span className="font-semibold">support@socialpulse.com</span>.
                </p>
              </div>
            </form>
          )}
        </div>

        {/* Aside — direct routes */}
        <aside className="space-y-4" data-testid="sp-contact-aside">
          <DirectCard
            icon={Mail}
            title="Email support"
            desc="For general questions & account help."
            href="mailto:support@socialpulse.com"
            hrefLabel="support@socialpulse.com"
            tint="violet"
          />
          <DirectCard
            icon={ShieldCheck}
            title="Trust & safety"
            desc="Urgent reports & takedown requests."
            href="mailto:trust@socialpulse.com"
            hrefLabel="trust@socialpulse.com"
            tint="rose"
          />
          <DirectCard
            icon={Building2}
            title="Legal & press"
            desc="Legal, copyright and press inquiries."
            href="mailto:legal@socialpulse.com"
            hrefLabel="legal@socialpulse.com"
            tint="blue"
          />
          <div
            className="rounded-2xl ring-1 ring-emerald-200 p-5"
            style={{ background: "linear-gradient(135deg,#ECFDF5,#F5F1FA)" }}
          >
            <p className="text-xs font-semibold uppercase tracking-[.22em] text-emerald-700">
              Mailing address
            </p>
            <p className="mt-2 text-sm text-slate-700 leading-relaxed">
              SocialPulse Inc.<br />
              1234 Community Way<br />
              San Jose, CA 95110, USA
            </p>
          </div>
        </aside>
      </main>

      <SocialPulseFooter />
    </div>
  );
}

/* ─────────────────── UI atoms ─────────────────── */

const INPUT_CLS =
  "w-full h-11 px-4 rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-500 focus:outline-none text-sm text-slate-800 placeholder:text-slate-400 bg-white transition-shadow";

const TINTS = {
  violet:   { bg: "bg-violet-50",  text: "text-violet-700",  ring: "ring-violet-200",  activeBg: "bg-violet-600",  activeText: "text-white" },
  emerald:  { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", activeBg: "bg-emerald-600", activeText: "text-white" },
  blue:     { bg: "bg-blue-50",    text: "text-blue-700",    ring: "ring-blue-200",    activeBg: "bg-blue-600",    activeText: "text-white" },
  orange:   { bg: "bg-orange-50",  text: "text-orange-700",  ring: "ring-orange-200",  activeBg: "bg-orange-600",  activeText: "text-white" },
  rose:     { bg: "bg-rose-50",    text: "text-rose-700",    ring: "ring-rose-200",    activeBg: "bg-rose-600",    activeText: "text-white" },
  teal:     { bg: "bg-teal-50",    text: "text-teal-700",    ring: "ring-teal-200",    activeBg: "bg-teal-600",    activeText: "text-white" },
};

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="block text-xs font-semibold uppercase tracking-[.15em] text-slate-500 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function TopicChip({ value, label, icon: Icon, tint, selected, onClick }) {
  const t = TINTS[tint] || TINTS.violet;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid={`sp-contact-topic-${value}`}
        aria-pressed={selected}
        className={`w-full flex items-center gap-2 h-11 px-3 rounded-xl text-xs font-semibold transition-all ring-1 ${
          selected
            ? `${t.activeBg} ${t.activeText} ring-transparent shadow-sm`
            : `${t.bg} ${t.text} ${t.ring} hover:brightness-95`
        }`}
      >
        <Icon className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate text-left">{label}</span>
      </button>
    </li>
  );
}

function DirectCard({ icon: Icon, title, desc, href, hrefLabel, tint }) {
  const t = TINTS[tint] || TINTS.violet;
  return (
    <a
      href={href}
      data-testid={`sp-contact-direct-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className="group flex items-start gap-3 rounded-2xl bg-white ring-1 ring-slate-200 p-4 hover:ring-violet-300 hover:shadow-md transition-all"
    >
      <span className={`w-10 h-10 grid place-items-center rounded-xl ${t.bg} ${t.text} ring-1 ${t.ring} shrink-0`}>
        <Icon className="w-4 h-4" strokeWidth={2.2} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-900 text-sm">{title}</p>
        <p className="mt-0.5 text-xs text-slate-500">{desc}</p>
        <p className={`mt-1 text-xs font-semibold ${t.text} truncate`}>{hrefLabel}</p>
      </div>
    </a>
  );
}

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}
